/**
 * Deriving SOG/COG from successive GPS fixes when the browser doesn't supply
 * `coords.speed` / `coords.heading` (common on old devices and at low speed).
 * Pure and framework-free — the web positionService feeds raw fixes in.
 */

import { haversineMeters, initialBearingDeg, normalizeBearing, METERS_PER_NM } from './geo.js';

export interface RawSample {
  lat: number;
  lon: number;
  accuracyM: number;
  ts: number; // epoch ms
}

export interface Motion {
  sogKn: number | null;
  cogDeg: number | null;
}

const EMA_ALPHA = 0.3;
const MAX_ACCURACY_M = 30;
const MAX_DT_MS = 5_000;

/**
 * Stateful EMA deriver. Emits null until it has a clean consecutive pair; emits
 * null again (rather than noise) when a sample is too inaccurate or too spaced
 * out. COG smoothing is done on the unit vector to avoid the 359°→1° wrap bug.
 */
export class MotionDeriver {
  private prev: RawSample | null = null;
  private sogEma: number | null = null;
  private cosEma = 0;
  private sinEma = 0;
  private hasHeading = false;

  reset(): void {
    this.prev = null;
    this.sogEma = null;
    this.cosEma = 0;
    this.sinEma = 0;
    this.hasHeading = false;
  }

  push(sample: RawSample): Motion {
    const prev = this.prev;
    this.prev = sample;

    if (!prev) return { sogKn: this.sogEma, cogDeg: this.currentCog() };

    const dtMs = sample.ts - prev.ts;
    if (
      dtMs <= 0 ||
      dtMs > MAX_DT_MS ||
      sample.accuracyM > MAX_ACCURACY_M ||
      prev.accuracyM > MAX_ACCURACY_M
    ) {
      // Gap or noise: keep last smoothed values but don't fold this sample in.
      return { sogKn: this.sogEma, cogDeg: this.currentCog() };
    }

    const distM = haversineMeters(prev, sample);
    const speedMs = distM / (dtMs / 1000);
    const sogKn = (speedMs * 3600) / METERS_PER_NM;
    this.sogEma = this.sogEma === null ? sogKn : EMA_ALPHA * sogKn + (1 - EMA_ALPHA) * this.sogEma;

    // Only update heading if we actually moved (bearing is undefined at rest).
    if (distM > 1) {
      const brg = (initialBearingDeg(prev, sample) * Math.PI) / 180;
      const c = Math.cos(brg);
      const s = Math.sin(brg);
      if (!this.hasHeading) {
        this.cosEma = c;
        this.sinEma = s;
        this.hasHeading = true;
      } else {
        this.cosEma = EMA_ALPHA * c + (1 - EMA_ALPHA) * this.cosEma;
        this.sinEma = EMA_ALPHA * s + (1 - EMA_ALPHA) * this.sinEma;
      }
    }

    return { sogKn: this.sogEma, cogDeg: this.currentCog() };
  }

  private currentCog(): number | null {
    if (!this.hasHeading) return null;
    return normalizeBearing((Math.atan2(this.sinEma, this.cosEma) * 180) / Math.PI);
  }
}

/**
 * Display gate for COG: bearing is meaningless below ~1 kn, so callers show "—".
 * Kept here so web and the future RN app share one rule.
 */
export function cogIsMeaningful(sogKn: number | null): boolean {
  return sogKn !== null && sogKn >= 1;
}
